import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, Check } from 'lucide-react';
import { cupLockComponents, cupLockAdvantages } from '../data/mockData';

const CupLock = () => {
  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="cuplock-page">
      {/* Hero (teal) */}
      <section className="relative py-24 md:py-32 overflow-hidden bg-gradient-to-br from-[#008080] via-[#0a7878] to-[#005a5a] text-white">
        <div className="absolute inset-0 bp-grid-on-dark opacity-50"></div>
        <div className="spotlight-gold bottom-0 right-0"></div>
        <span className="serial-ghost serial-ghost-light absolute right-4 top-12 text-[12rem] hidden md:block">CUPLOCK</span>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="ark-overline ark-overline-light">// system · ARK-CL</span>
          <h1 className="font-display font-black uppercase tracking-tighter text-white mt-5 text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.88]">
            Cuplock<br />
            <span className="text-[#DAA520]">Scaffolding System.</span>
          </h1>
          <p className="text-white/85 mt-6 max-w-2xl text-base md:text-lg">
            Advanced modular scaffolding system for complex construction projects. Fast lock, high load capacity, certified safe.
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="relative py-20 md:py-24 bg-white">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <span className="ark-overline">// 01 · Overview</span>
            <h2 className="font-display font-bold text-[#008080] text-3xl md:text-5xl mt-4 leading-[0.95]">What is the Cuplock system?</h2>
            <div className="mt-6 space-y-4 text-gray-700 leading-relaxed">
              <p>
                The Cuplock Scaffolding System is a modular scaffolding system known for its fast locking mechanism, high load-bearing capacity, and safety.
              </p>
              <p>
                It uses a cup-and-blade locking arrangement that allows quick connection of ledgers and braces to vertical standards — ideal for buildings, bridges, industrial plants and infrastructure projects.
              </p>
            </div>
          </div>
          <div className="lg:col-span-7 ark-card-beam bg-gray-50 border border-gray-200 p-4 rounded">
            <img
              src="https://customer-assets.emergentagent.com/job_construction-hire/artifacts/o9axj8wg_6aa04f58-95f3-4283-a027-9c4170ecc676.jpeg"
              alt="Cuplock System diagram"
              className="w-full h-auto object-contain"
            />
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="relative py-20 md:py-24 bg-gray-50 border-t border-gray-200">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="ark-overline">// 02 · Advantages</span>
          <h2 className="font-display font-bold text-[#008080] text-4xl md:text-5xl mt-4 mb-10 leading-[0.95]">Why teams pick Cuplock.</h2>
          <div className="grid sm:grid-cols-2 gap-px bg-gray-200">
            {cupLockAdvantages.map((advantage, i) => (
              <div key={i} className="ark-card-beam bg-white p-7 flex items-start gap-4">
                <div className="w-9 h-9 border border-[#DAA520]/50 bg-[#DAA520]/5 flex items-center justify-center flex-shrink-0 rounded">
                  <Check size={16} className="text-[#DAA520]" />
                </div>
                <div>
                  <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-gray-400">// {String(i + 1).padStart(2, '0')}</span>
                  <p className="font-display font-medium text-gray-900 text-lg mt-1">{advantage}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Components */}
      <section className="relative py-20 md:py-24 bg-white border-t border-gray-200">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="ark-overline">// 03 · Components</span>
          <h2 className="font-display font-bold text-[#008080] text-4xl md:text-5xl mt-4 mb-10 leading-[0.95]">Main components.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-gray-200">
            {cupLockComponents.map((component, i) => (
              <div key={i} className="ark-card-beam bg-white p-7 text-center min-h-[200px] flex flex-col items-center justify-center">
                <div className="font-display font-black text-[#DAA520] text-5xl md:text-6xl mb-3">{String(i + 1).padStart(2, '0')}</div>
                <p className="text-gray-900 text-sm leading-snug">{component}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className="relative py-20 md:py-24 bg-gray-50 border-t border-gray-200">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <span className="ark-overline">// 04 · Applications</span>
            <h2 className="font-display font-bold text-[#008080] text-3xl md:text-5xl mt-4 leading-[0.95]">Where it shines.</h2>
          </div>
          <div className="lg:col-span-8 space-y-px bg-gray-200">
            {[
              'Residential and commercial building construction',
              'Bridge construction and infrastructure projects',
              'Industrial plants and refineries',
              'Complex architectural structures',
            ].map((a, i) => (
              <div key={i} className="bg-white p-6 flex items-center gap-5">
                <span className="font-mono text-xs tracking-[0.25em] text-[#008080] uppercase w-16">// {String(i + 1).padStart(2, '0')}</span>
                <span className="text-gray-900 text-lg">{a}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA (teal) */}
      <section className="relative py-20 md:py-24 overflow-hidden bg-gradient-to-br from-[#008080] to-[#005a5a] text-white">
        <div className="absolute inset-0 bp-grid-on-dark opacity-40"></div>
        <div className="spotlight-gold left-1/2 -translate-x-1/2 -top-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display font-black uppercase tracking-tighter text-white text-4xl md:text-6xl leading-[0.9]">
            Interested in <span className="text-[#DAA520]">Cuplock</span>?
          </h2>
          <p className="text-white/85 mt-5">Contact us for availability and pricing.</p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <a href="tel:+919766601155" className="ark-btn-primary"><Phone size={16} /> Call Now</a>
            <a href="https://wa.me/919766601155" target="_blank" rel="noopener noreferrer" className="ark-btn-secondary ark-btn-secondary-dark"><MessageCircle size={16} /> WhatsApp Us</a>
            <Link to="/contact?product=Cuplock&source=product_card" className="inline-flex items-center gap-2 px-6 py-4 text-sm font-mono uppercase tracking-widest font-semibold text-white border border-white/30 hover:bg-white/10 transition rounded">Get Quote</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CupLock;
